import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';

// Multi-person picker that behaves like the project selector: a trigger showing
// the current selection as pills, and a searchable popover of people not yet
// picked (selected people are removed from the list — remove them from the
// field instead). The popover stays open while you pick several people.
// `locked` ids show as pills without a remove button (`lockedHint` says why).
export function UserMultiSelect({
  users,
  selected,
  onChange,
  locked = [],
  lockedHint,
  placeholder = 'Select people',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const byId = useMemo(
    () => new Map(users.map((user) => [user.id, user])),
    [users],
  );
  // Show every currently-assigned person as a chip — including a deactivated
  // one, so it can still be removed (a full-replacement save keeps submitting
  // a hidden id otherwise). The dropdown only offers active people.
  const selectedUsers = selected.map((id) => byId.get(id)).filter(Boolean);

  useEffect(() => {
    if (!open) return undefined;
    function onDocMouseDown(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  const needle = query.trim().toLowerCase();
  const available = users.filter(
    (user) => user.active && !selected.includes(user.id),
  );
  const filtered = needle
    ? available.filter((user) => user.name.toLowerCase().includes(needle))
    : available;

  const toggle = (id) =>
    onChange(
      selected.includes(id)
        ? selected.filter((item) => item !== id)
        : [...selected, id],
    );

  return (
    <div className="user-picker" ref={ref}>
      <div
        className="user-picker-trigger"
        role="button"
        tabIndex={0}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setOpen((value) => !value);
          }
        }}
      >
        <span className="user-picker-value">
          {selectedUsers.length === 0 ? (
            <span className="muted-text">{placeholder}</span>
          ) : (
            selectedUsers.map((user) =>
              locked.includes(user.id) ? (
                <span
                  key={user.id}
                  className={`user-pill is-locked${user.active ? '' : ' is-inactive'}`}
                  data-tooltip={lockedHint}
                  onClick={(event) => event.stopPropagation()}
                >
                  {user.name}
                  {!user.active && ' (inactive)'}
                </span>
              ) : (
                <button
                  type="button"
                  key={user.id}
                  className={`user-pill${user.active ? '' : ' is-inactive'}`}
                  aria-label={`Remove ${user.name}`}
                  data-tooltip={`Remove ${user.name}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggle(user.id);
                  }}
                >
                  {user.name}
                  {!user.active && ' (inactive)'}
                  <X size={11} aria-hidden="true" />
                </button>
              ),
            )
          )}
        </span>
        <ChevronDown size={14} />
      </div>

      {open && (
        <div className="user-picker-popover">
          <label className="project-picker-search">
            <Search size={14} />
            <input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search people..."
            />
          </label>
          <div className="user-picker-list">
            {filtered.map((user) => (
              <button
                type="button"
                key={user.id}
                className="user-option"
                onClick={() => toggle(user.id)}
              >
                <span className="truncate">{user.name}</span>
              </button>
            ))}
            {filtered.length === 0 && (
              <em className="user-picker-empty">
                {available.length === 0
                  ? 'Everyone is already selected'
                  : 'No people found'}
              </em>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
